// src/pages/Aide.tsx
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  FaArrowRight,
  FaBookOpen,
  FaBriefcase,
  FaCalendarCheck,
  FaCheckCircle,
  FaCommentDots,
  FaChevronDown,
  FaChevronUp,
  FaEnvelope,
  FaExclamationTriangle,
  FaGift,
  FaHeadset,
  FaLock,
  FaMagic,
  FaMapMarkedAlt,
  FaMobileAlt,
  FaQrcode,
  FaQuestionCircle,
  FaReceipt,
  FaSearch,
  FaShieldAlt,
  FaShoppingBag,
  FaStore,
  FaTicketAlt,
  FaUserCircle,
} from 'react-icons/fa';

type HelpCategoryKey =
  | 'traveler'
  | 'purchases'
  | 'reservations'
  | 'merchant'
  | 'admin'
  | 'security';

type HelpArticle = {
  q: string;
  a: string;
  category: HelpCategoryKey;
  tags: string[];
  cta?: {
    label: string;
    to: string;
  };
};

type HelpCategory = {
  key: HelpCategoryKey;
  label: string;
  desc: string;
  icon: React.ReactNode;
  accent: string;
};

const helpCopy = {
  fr: {
    seoTitle: 'Centre d’aide Forfeo Explore',
    badge: 'Centre d’aide Forfeo',
    title: 'Comment peut-on t’aider ?',
    subtitle:
      'Réponses claires pour les achats, vouchers, réservations, offres Last Call, alertes Évasion, compte voyageur et validation marchand.',
    searchPlaceholder: 'Rechercher : voucher, reçu, réservation, scan, commerçant...',
    popular: 'Accès rapides',
    allTopics: 'Toutes les réponses',
    noResultTitle: 'Aucun résultat trouvé',
    noResultText: 'Essaie avec un autre mot-clé ou contacte notre équipe.',
    contactTitle: 'Besoin d’un humain ?',
    contactText:
      'Notre équipe peut t’aider avec un paiement, un voucher, une réservation ou un accès marchand.',
    contactButton: 'Écrire à Forfeo',
    backOffers: 'Explorer les offres',
    updated: 'Mis à jour pour les achats, vouchers et validations marchands Forfeo Explore.',
    categories: [
      {
        key: 'traveler',
        label: 'Compte voyageur',
        desc: 'Connexion, espace voyageur, favoris et alertes.',
        accent: 'from-blue-500 to-cyan-500',
      },
      {
        key: 'purchases',
        label: 'Achats & vouchers',
        desc: 'Mes achats, billets QR, reçus PDF et statuts.',
        accent: 'from-purple-500 to-fuchsia-500',
      },
      {
        key: 'reservations',
        label: 'Réservations',
        desc: 'Last Call, révélation partenaire et confirmations.',
        accent: 'from-orange-500 to-red-500',
      },
      {
        key: 'merchant',
        label: 'Commerçants',
        desc: 'Scanner caméra, validation voucher et tableau de bord.',
        accent: 'from-emerald-500 to-teal-500',
      },
      {
        key: 'admin',
        label: 'Support admin',
        desc: 'Commandes, reçus, vouchers et actions de secours.',
        accent: 'from-slate-700 to-slate-950',
      },
      {
        key: 'security',
        label: 'Sécurité',
        desc: 'Paiements, confidentialité, courriels et données.',
        accent: 'from-indigo-500 to-blue-700',
      },
    ] as Omit<HelpCategory, 'icon'>[],
    quickLinks: [
      { label: 'Mes achats', to: '/mes-achats', icon: 'shopping' },
      { label: 'Mes réservations', to: '/mes-reservations', icon: 'calendar' },
      { label: 'Tableau voyageur', to: '/dashboard-voyageur', icon: 'user' },
      { label: 'Portail commerçant', to: '/business-dashboard', icon: 'store' },
      { label: 'Découvrir le Québec', to: '/decouvrir-quebec', icon: 'map' },
      { label: 'Offrir un moment', to: '/offrir-un-moment', icon: 'gift' },
      { label: 'Calendrier des occasions', to: '/calendrier-des-occasions', icon: 'calendar' },
      { label: 'Révélation surprise', to: '/revelation-surprise', icon: 'magic' },
      { label: 'Avis après expérience', to: '/avis-experience', icon: 'review' },
      { label: 'Avis publics', to: '/avis-publics', icon: 'review' },
      { label: 'Carte cadeau', to: '/carte-cadeau', icon: 'gift' },
    ],
    articles: [
      {
        category: 'purchases',
        q: 'Comment laisser un avis après une expérience ?',
        a: 'Après avoir utilisé un voucher, complété une réservation ou vécu une expérience Forfeo, ouvrez la page Avis après expérience. Vous pouvez entrer votre code FE, votre courriel d’achat, une note et un commentaire. Les avis publics sont modérés avant diffusion.',
        tags: ['avis', 'expérience', 'note', 'commentaire', 'voucher', 'qualité'],
        cta: { label: 'Laisser un avis', to: '/avis-experience' },
      },

      {
        category: 'purchases',
        q: 'Où consulter les avis publics Forfeo ?',
        a: 'Les avis approuvés par Forfeo sont regroupés dans la page Avis publics. Les commentaires privés, rejetés ou en attente de modération ne sont jamais affichés publiquement.',
        tags: ['avis publics', 'réputation', 'partenaire', 'confiance'],
        cta: { label: 'Voir les avis publics', to: '/avis-publics' },
      },

      {
        category: 'purchases',
        q: 'À quoi sert le calendrier des occasions ?',
        a: 'Le calendrier des occasions aide à choisir le bon moment à offrir selon la période de l’année : anniversaire, Saint-Valentin, fête des Mères, relâche, cadeau corporatif ou temps des fêtes. Il inspire sur Forfeo Explore, puis redirige vers forfeo.com lorsque l’achat officiel de la carte virtuelle est prêt.',
        tags: ['calendrier', 'occasion', 'cadeau', 'carte', 'forfeo.com'],
        cta: { label: 'Voir le calendrier', to: '/calendrier-des-occasions' },
      },

      {
        category: 'purchases',
        q: 'À quoi sert la révélation surprise ?',
        a: 'La révélation surprise crée une animation émotionnelle après l’achat ou avant d’offrir une carte Forfeo. Elle ne remplace pas le voucher officiel : le billet, le reçu et les statuts restent dans Mes achats ou sur forfeo.com selon le type d’achat.',
        tags: ['révélation', 'surprise', 'cadeau', 'voucher', 'forfeo.com'],
        cta: { label: 'Ouvrir la révélation surprise', to: '/revelation-surprise' },
      },
      {
        category: 'traveler',
        q: 'Où retrouver mon espace voyageur ?',
        a: 'Une fois connecté, ouvre le menu Espace connecté, puis clique sur Tableau voyageur. Tu peux aussi aller directement dans /dashboard-voyageur pour voir tes achats, réservations, favoris et alertes.',
        tags: ['compte', 'connexion', 'voyageur', 'dashboard'],
        cta: { label: 'Ouvrir mon tableau voyageur', to: '/dashboard-voyageur' },
      },
      {
        category: 'traveler',
        q: 'Pourquoi je ne vois pas mes billets dans le Club Forfeo ?',
        a: 'Le Club Forfeo sert maintenant de hub voyageur. Les vrais billets et vouchers sont centralisés dans Mes achats. Si tu arrives depuis un ancien onglet du Club, Forfeo te redirige progressivement vers la bonne page.',
        tags: ['club', 'billet', 'voucher', 'mes achats'],
        cta: { label: 'Voir mes achats', to: '/mes-achats' },
      },
      {
        category: 'traveler',
        q: 'Comment changer la langue du site ?',
        a: 'Utilise le sélecteur FR/EN dans le header. Les pages transactionnelles importantes comme Mes achats, Voucher, Réservations et le tableau marchand ont été adaptées en français et en anglais.',
        tags: ['fr', 'en', 'langue', 'anglais'],
      },
      {
        category: 'purchases',
        q: 'Où voir mes achats et mes vouchers ?',
        a: 'Va dans Mes achats. Tu y verras tes commandes, les vouchers associés, leur statut et les liens vers les billets QR officiels.',
        tags: ['achat', 'voucher', 'commande', 'billet'],
        cta: { label: 'Ouvrir mes achats', to: '/mes-achats' },
      },
      {
        category: 'purchases',
        q: 'Que signifie le statut de mon voucher ?',
        a: 'Actif signifie que le voucher peut être présenté au partenaire. Utilisé signifie qu’un commerçant l’a validé. Expiré signifie que la période d’utilisation est dépassée. Annulé signifie que Forfeo ou le support l’a désactivé.',
        tags: ['actif', 'utilisé', 'expiré', 'annulé', 'statut'],
      },
      {
        category: 'purchases',
        q: 'Puis-je télécharger mon billet ou mon reçu ?',
        a: 'Oui. Depuis la page du voucher, tu peux télécharger ton billet PDF. Depuis Mes achats, tu peux télécharger ton reçu PDF lorsque l’information de commande est disponible.',
        tags: ['pdf', 'reçu', 'billet', 'télécharger'],
      },
      {
        category: 'purchases',
        q: 'Je n’ai pas reçu mon courriel de reçu, que faire ?',
        a: 'Vérifie tes indésirables, promotions ou spam. Si le courriel est introuvable, contacte Forfeo. L’équipe peut vérifier la commande et renvoyer le reçu depuis le centre admin.',
        tags: ['courriel', 'email', 'reçu', 'resend'],
      },
      {
        category: 'reservations',
        q: 'Quand le partenaire Last Call est-il révélé ?',
        a: 'Pour les offres confidentielles, l’identité du partenaire peut rester cachée jusqu’à la confirmation. Après réservation, Forfeo affiche ou envoie les détails utiles : lieu, adresse, téléphone et lien Google Maps.',
        tags: ['last call', 'révélation', 'partenaire', 'secret'],
      },
      {
        category: 'reservations',
        q: 'Est-ce que Forfeo organise l’activité ?',
        a: 'Non. Forfeo est une plateforme technologique. Les expériences, disponibilités et services sur place sont offerts et gérés par les entreprises partenaires.',
        tags: ['activité', 'partenaire', 'responsabilité', 'expérience'],
      },
      {
        category: 'reservations',
        q: 'Où voir mes réservations ?',
        a: 'Toutes les réservations compatibles avec l’espace voyageur se retrouvent dans Mes réservations. Les confirmations importantes peuvent aussi être envoyées par courriel.',
        tags: ['réservation', 'confirmation', 'last call'],
        cta: { label: 'Voir mes réservations', to: '/mes-reservations' },
      },
      {
        category: 'merchant',
        q: 'Comment un commerçant valide un voucher ?',
        a: 'Dans le BusinessDashboard, le commerçant peut scanner le QR code avec la caméra ou entrer le code manuellement. Le système valide le voucher, affiche son statut et permet de le marquer comme utilisé si tout est conforme.',
        tags: ['commerçant', 'scanner', 'qr', 'camera', 'voucher'],
        cta: { label: 'Ouvrir le portail commerçant', to: '/business-dashboard' },
      },
      {
        category: 'merchant',
        q: 'La caméra ne fonctionne pas dans le dashboard marchand',
        a: 'La caméra fonctionne seulement sur un site sécurisé HTTPS et demande une permission du navigateur. Essaie Chrome ou Edge, autorise la caméra, puis recharge la page. Le champ manuel reste disponible en secours.',
        tags: ['camera', 'scanner', 'permission', 'navigateur'],
      },
      {
        category: 'merchant',
        q: 'Un voucher déjà utilisé peut-il être utilisé une deuxième fois ?',
        a: 'Non. Une fois validé, le voucher passe au statut Utilisé. Le client le voit aussi comme utilisé dans son espace, et Forfeo garde une trace dans le journal de validation.',
        tags: ['double', 'utilisé', 'redeemed', 'audit'],
      },
      {
        category: 'admin',
        q: 'Que peut faire l’admin Forfeo dans le centre Paiements ?',
        a: 'L’admin peut voir les commandes, vouchers, validations, anomalies, détails de commande, renvoyer un reçu, régénérer des vouchers manquants et annuler un voucher avec une raison.',
        tags: ['admin', 'paiement', 'commande', 'action', 'support'],
      },
      {
        category: 'admin',
        q: 'Pourquoi une commande payée peut apparaître sans voucher ?',
        a: 'Cela peut arriver si le webhook Stripe a été interrompu, si les items de commande sont incomplets ou si une erreur de génération est survenue. Le centre admin permet de diagnostiquer et régénérer les vouchers manquants.',
        tags: ['webhook', 'stripe', 'anomalie', 'voucher manquant'],
      },
      {
        category: 'admin',
        q: 'À quoi sert le journal d’audit ?',
        a: 'Le journal d’audit garde les traces des validations marchand et des actions admin. Il aide Forfeo à répondre aux litiges, erreurs de scan, annulations ou demandes de support.',
        tags: ['audit', 'preuve', 'litige', 'journal'],
      },
      {
        category: 'security',
        q: 'Les paiements sont-ils sécurisés ?',
        a: 'Les paiements sont traités par Stripe. Forfeo reçoit les confirmations nécessaires pour créer les commandes, reçus et vouchers, sans exposer les informations sensibles de carte bancaire dans le frontend.',
        tags: ['sécurité', 'stripe', 'paiement', 'carte'],
      },
      {
        category: 'security',
        q: 'Pourquoi Forfeo utilise des liens sécurisés de voucher ?',
        a: 'Chaque voucher peut utiliser un token sécurisé ou un code FE. Cela permet d’afficher un billet officiel, d’éviter les validations non autorisées et de protéger la logique de scan marchand.',
        tags: ['token', 'sécurisé', 'qr', 'fe'],
      },
      {
        category: 'security',
        q: 'Comment contacter Forfeo pour un problème sensible ?',
        a: 'Pour un problème de paiement, accès, courriel ou validation, écris à client@forfeo.com en indiquant ton courriel d’achat, le numéro de commande si disponible et une capture d’écran du problème.',
        tags: ['support', 'client', 'problème', 'sécurité'],
      },
    ] as HelpArticle[],
  },
  en: {
    seoTitle: 'Forfeo Explore Help Center',
    badge: 'Forfeo Help Center',
    title: 'How can we help?',
    subtitle:
      'Clear answers for purchases, vouchers, reservations, Last Call offers, Escape alerts, traveler accounts and merchant validation.',
    searchPlaceholder: 'Search: voucher, receipt, reservation, scan, merchant...',
    popular: 'Quick access',
    allTopics: 'All answers',
    noResultTitle: 'No result found',
    noResultText: 'Try another keyword or contact our team.',
    contactTitle: 'Need a human?',
    contactText:
      'Our team can help with a payment, voucher, reservation or merchant access issue.',
    contactButton: 'Email Forfeo',
    backOffers: 'Explore offers',
    updated: 'Updated for Forfeo Explore purchases, vouchers and merchant validations.',
    categories: [
      {
        key: 'traveler',
        label: 'Traveler account',
        desc: 'Login, traveler space, favorites and alerts.',
        accent: 'from-blue-500 to-cyan-500',
      },
      {
        key: 'purchases',
        label: 'Purchases & vouchers',
        desc: 'My purchases, QR tickets, PDF receipts and statuses.',
        accent: 'from-purple-500 to-fuchsia-500',
      },
      {
        key: 'reservations',
        label: 'Reservations',
        desc: 'Last Call, partner reveal and confirmations.',
        accent: 'from-orange-500 to-red-500',
      },
      {
        key: 'merchant',
        label: 'Merchants',
        desc: 'Camera scanner, voucher validation and dashboard.',
        accent: 'from-emerald-500 to-teal-500',
      },
      {
        key: 'admin',
        label: 'Admin support',
        desc: 'Orders, receipts, vouchers and recovery actions.',
        accent: 'from-slate-700 to-slate-950',
      },
      {
        key: 'security',
        label: 'Security',
        desc: 'Payments, privacy, emails and data.',
        accent: 'from-indigo-500 to-blue-700',
      },
    ] as Omit<HelpCategory, 'icon'>[],
    quickLinks: [
      { label: 'My purchases', to: '/mes-achats', icon: 'shopping' },
      { label: 'My reservations', to: '/mes-reservations', icon: 'calendar' },
      { label: 'Traveler dashboard', to: '/dashboard-voyageur', icon: 'user' },
      { label: 'Merchant portal', to: '/business-dashboard', icon: 'store' },
      { label: 'Discover Quebec', to: '/discover-quebec', icon: 'map' },
      { label: 'Gift a moment', to: '/gift-a-moment', icon: 'gift' },
      { label: 'Occasion calendar', to: '/occasion-calendar', icon: 'calendar' },
      { label: 'Surprise reveal', to: '/surprise-reveal', icon: 'magic' },
      { label: 'Post-experience review', to: '/experience-review', icon: 'review' },
      { label: 'Gift card', to: '/carte-cadeau', icon: 'gift' },
    ],
    articles: [
      {
        category: 'purchases',
        q: 'How do I leave a review after an experience?',
        a: 'After using a voucher, completing a reservation or enjoying a Forfeo experience, open the Post-experience review page. You can enter your FE code, purchase email, rating and comment. Public reviews are moderated before publication.',
        tags: ['review', 'experience', 'rating', 'comment', 'voucher', 'quality'],
        cta: { label: 'Leave a review', to: '/experience-review' },
      },

      {
        category: 'purchases',
        q: 'What is the occasion calendar for?',
        a: 'The occasion calendar helps users choose the right gift moment throughout the year: birthdays, Valentine’s Day, Mother’s Day, school break, corporate gifting or holidays. It inspires on Forfeo Explore, then redirects to forfeo.com when the official virtual card purchase is ready.',
        tags: ['calendar', 'occasion', 'gift', 'card', 'forfeo.com'],
        cta: { label: 'Open occasion calendar', to: '/occasion-calendar' },
      },

      {
        category: 'purchases',
        q: 'What is the surprise reveal for?',
        a: 'The surprise reveal creates an emotional animation after a purchase or before gifting a Forfeo card. It does not replace the official voucher: the ticket, receipt and status remain in My purchases or on forfeo.com depending on the purchase type.',
        tags: ['reveal', 'surprise', 'gift', 'voucher', 'forfeo.com'],
        cta: { label: 'Open surprise reveal', to: '/surprise-reveal' },
      },
      {
        category: 'traveler',
        q: 'Where can I find my traveler space?',
        a: 'Once signed in, open the Connected space menu and choose Traveler dashboard. You can also go directly to /dashboard-voyageur to see your purchases, reservations, favorites and alerts.',
        tags: ['account', 'login', 'traveler', 'dashboard'],
        cta: { label: 'Open traveler dashboard', to: '/dashboard-voyageur' },
      },
      {
        category: 'traveler',
        q: 'Why do I not see my tickets in Club Forfeo?',
        a: 'Club Forfeo is now a traveler hub. Your actual tickets and vouchers are centralized in My purchases. Older Club tabs are progressively redirected to the right pages.',
        tags: ['club', 'ticket', 'voucher', 'my purchases'],
        cta: { label: 'View my purchases', to: '/mes-achats' },
      },
      {
        category: 'traveler',
        q: 'How do I change the site language?',
        a: 'Use the FR/EN language switcher in the header. Important transactional pages such as My purchases, Voucher, Reservations and Merchant dashboard have been adapted in French and English.',
        tags: ['fr', 'en', 'language', 'english'],
      },
      {
        category: 'purchases',
        q: 'Where can I see my purchases and vouchers?',
        a: 'Go to My purchases. You will see your orders, related vouchers, their status and links to the official QR tickets.',
        tags: ['purchase', 'voucher', 'order', 'ticket'],
        cta: { label: 'Open my purchases', to: '/mes-achats' },
      },
      {
        category: 'purchases',
        q: 'What does my voucher status mean?',
        a: 'Active means the voucher can be shown to the partner. Redeemed means a merchant validated it. Expired means the usage period has passed. Cancelled means Forfeo or support disabled it.',
        tags: ['active', 'redeemed', 'expired', 'cancelled', 'status'],
      },
      {
        category: 'purchases',
        q: 'Can I download my ticket or receipt?',
        a: 'Yes. From the voucher page, you can download your PDF ticket. From My purchases, you can download your PDF receipt when order information is available.',
        tags: ['pdf', 'receipt', 'ticket', 'download'],
      },
      {
        category: 'purchases',
        q: 'I did not receive my receipt email. What should I do?',
        a: 'Check spam, promotions or junk folders. If you cannot find it, contact Forfeo. The team can verify the order and resend the receipt from the admin center.',
        tags: ['email', 'receipt', 'resend'],
      },
      {
        category: 'reservations',
        q: 'When is the Last Call partner revealed?',
        a: 'For confidential offers, the partner identity can remain hidden until confirmation. After booking, Forfeo displays or sends useful details: place, address, phone and Google Maps link.',
        tags: ['last call', 'reveal', 'partner', 'secret'],
      },
      {
        category: 'reservations',
        q: 'Does Forfeo organize the activity?',
        a: 'No. Forfeo is a technology platform. Experiences, availability and on-site services are offered and managed by partner businesses.',
        tags: ['activity', 'partner', 'responsibility', 'experience'],
      },
      {
        category: 'reservations',
        q: 'Where can I see my reservations?',
        a: 'All compatible traveler reservations are found in My reservations. Important confirmations can also be sent by email.',
        tags: ['reservation', 'confirmation', 'last call'],
        cta: { label: 'View my reservations', to: '/mes-reservations' },
      },
      {
        category: 'merchant',
        q: 'How does a merchant validate a voucher?',
        a: 'In the BusinessDashboard, the merchant can scan the QR code with the camera or enter the code manually. The system validates the voucher, shows its status and allows marking it as redeemed when everything is valid.',
        tags: ['merchant', 'scanner', 'qr', 'camera', 'voucher'],
        cta: { label: 'Open merchant portal', to: '/business-dashboard' },
      },
      {
        category: 'merchant',
        q: 'The camera does not work in the merchant dashboard',
        a: 'The camera works only on a secure HTTPS website and requires browser permission. Try Chrome or Edge, allow camera access and reload the page. Manual entry remains available as a fallback.',
        tags: ['camera', 'scanner', 'permission', 'browser'],
      },
      {
        category: 'merchant',
        q: 'Can a redeemed voucher be used twice?',
        a: 'No. Once validated, the voucher moves to Redeemed. The customer also sees it as redeemed, and Forfeo keeps a trace in the validation log.',
        tags: ['double', 'redeemed', 'audit'],
      },
      {
        category: 'admin',
        q: 'What can the Forfeo admin do in the Payments center?',
        a: 'The admin can view orders, vouchers, validations, anomalies, order details, resend a receipt, regenerate missing vouchers and cancel a voucher with a reason.',
        tags: ['admin', 'payment', 'order', 'action', 'support'],
      },
      {
        category: 'admin',
        q: 'Why can a paid order appear without a voucher?',
        a: 'This can happen if the Stripe webhook was interrupted, if order items are incomplete or if a generation error occurred. The admin center helps diagnose and regenerate missing vouchers.',
        tags: ['webhook', 'stripe', 'anomaly', 'missing voucher'],
      },
      {
        category: 'admin',
        q: 'What is the audit log for?',
        a: 'The audit log keeps traces of merchant validations and admin actions. It helps Forfeo handle disputes, scan errors, cancellations and support requests.',
        tags: ['audit', 'proof', 'dispute', 'log'],
      },
      {
        category: 'security',
        q: 'Are payments secure?',
        a: 'Payments are processed by Stripe. Forfeo receives the confirmations needed to create orders, receipts and vouchers, without exposing sensitive card details in the frontend.',
        tags: ['security', 'stripe', 'payment', 'card'],
      },
      {
        category: 'security',
        q: 'Why does Forfeo use secure voucher links?',
        a: 'Each voucher can use a secure token or FE code. This displays an official ticket, prevents unauthorized validation and protects the merchant scan flow.',
        tags: ['token', 'secure', 'qr', 'fe'],
      },
      {
        category: 'security',
        q: 'How do I contact Forfeo for a sensitive issue?',
        a: 'For payment, access, email or validation issues, write to client@forfeo.com with your purchase email, order number if available and a screenshot of the problem.',
        tags: ['support', 'client', 'issue', 'security'],
      },
    ] as HelpArticle[],
  },
};

const categoryIcon = (key: HelpCategoryKey) => {
  if (key === 'traveler') return <FaUserCircle />;
  if (key === 'purchases') return <FaTicketAlt />;
  if (key === 'reservations') return <FaCalendarCheck />;
  if (key === 'merchant') return <FaStore />;
  if (key === 'admin') return <FaBriefcase />;
  return <FaShieldAlt />;
};

const quickIcon = (key: string) => {
  if (key === 'shopping') return <FaShoppingBag />;
  if (key === 'calendar') return <FaCalendarCheck />;
  if (key === 'user') return <FaUserCircle />;
  if (key === 'store') return <FaStore />;
  if (key === 'map') return <FaMapMarkedAlt />;
  if (key === 'gift') return <FaGift />;
  if (key === 'magic') return <FaMagic />;
  if (key === 'review') return <FaCommentDots />;
  return <FaArrowRight />;
};

export default function Aide() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const t = helpCopy[lang];
  const categories: HelpCategory[] = t.categories.map((category) => ({
    ...category,
    icon: categoryIcon(category.key),
  }));

  const [open, setOpen] = useState(0);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<HelpCategoryKey | 'all'>('all');

  const normalizedQuery = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    return t.articles.filter((item) => {
      const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
      const searchable = `${item.q} ${item.a} ${item.tags.join(' ')}`.toLowerCase();
      const matchesQuery = !normalizedQuery || searchable.includes(normalizedQuery);
      return matchesCategory && matchesQuery;
    });
  }, [activeCategory, normalizedQuery, t.articles]);

  const stats = [
    { icon: <FaQrcode />, label: lang === 'en' ? 'Voucher QR' : 'Vouchers QR' },
    { icon: <FaReceipt />, label: lang === 'en' ? 'PDF receipts' : 'Reçus PDF' },
    { icon: <FaMobileAlt />, label: lang === 'en' ? 'Mobile ready' : 'Mobile prêt' },
    { icon: <FaLock />, label: lang === 'en' ? 'Secure flows' : 'Flux sécurisés' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <Header />
      <main className="pt-24 pb-24">
        <section className="relative overflow-hidden px-4 py-20 md:py-28 bg-slate-950 text-white">
          <div className="absolute inset-0 opacity-40">
            <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-blue-500 blur-[90px]" />
            <div className="absolute top-20 right-0 h-80 w-80 rounded-full bg-cyan-400 blur-[100px]" />
            <div className="absolute bottom-0 left-1/2 h-72 w-72 rounded-full bg-purple-500 blur-[110px]" />
          </div>
          <div className="absolute inset-0 opacity-[0.06] [background-image:linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] [background-size:34px_34px]" />

          <div className="relative z-10 max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur-xl px-4 py-2 rounded-full text-[11px] font-black uppercase tracking-[0.22em] text-cyan-100 mb-7">
              <FaQuestionCircle /> {t.badge}
            </div>
            <h1 className="text-4xl sm:text-6xl md:text-7xl font-black tracking-tighter mb-6 leading-[0.95]">
              {t.title}
            </h1>
            <p className="text-base md:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10">
              {t.subtitle}
            </p>

            <div className="max-w-3xl mx-auto bg-white/95 text-slate-900 border border-white/20 rounded-[2rem] p-3 shadow-2xl flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <FaSearch />
              </div>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t.searchPlaceholder}
                className="bg-transparent outline-none w-full font-bold text-sm md:text-base placeholder:text-slate-400"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto mt-8">
              {stats.map((item) => (
                <div key={item.label} className="bg-white/10 border border-white/10 rounded-2xl px-4 py-3 flex items-center justify-center gap-2 text-xs font-black uppercase tracking-widest text-slate-200">
                  <span className="text-cyan-300">{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 -mt-10 relative z-20">
          <div className="bg-white rounded-[2rem] border border-slate-100 shadow-2xl shadow-slate-200/60 p-4 md:p-6">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg md:text-xl font-black text-slate-900">{t.popular}</h2>
                <p className="text-xs text-slate-500 font-bold mt-1">{t.updated}</p>
              </div>
              <Link to="/boutique" className="hidden md:inline-flex items-center gap-2 bg-slate-950 text-white px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition">
                {t.backOffers} <FaArrowRight />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {t.quickLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="group rounded-2xl border border-slate-100 bg-slate-50 hover:bg-white hover:border-blue-200 hover:shadow-xl transition p-4 min-h-[118px] flex flex-col justify-between"
                >
                  <div className="w-11 h-11 rounded-xl bg-white group-hover:bg-blue-50 text-blue-600 flex items-center justify-center shadow-sm transition">
                    {quickIcon(link.icon)}
                  </div>
                  <div className="text-sm font-black text-slate-900 leading-tight mt-3 flex items-end justify-between gap-2">
                    <span>{link.label}</span>
                    <FaArrowRight className="text-slate-300 group-hover:text-blue-600 shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 py-12 md:py-16">
          <div className="grid lg:grid-cols-[330px_1fr] gap-8 items-start">
            <aside className="lg:sticky lg:top-28 space-y-3">
              <button
                onClick={() => setActiveCategory('all')}
                className={`w-full text-left rounded-2xl border px-4 py-4 transition ${
                  activeCategory === 'all'
                    ? 'bg-slate-950 text-white border-slate-950 shadow-xl'
                    : 'bg-white text-slate-700 border-slate-100 hover:border-blue-200 hover:shadow-md'
                }`}
              >
                <div className="flex items-center gap-3 font-black">
                  <FaBookOpen className={activeCategory === 'all' ? 'text-cyan-300' : 'text-blue-600'} />
                  {t.allTopics}
                </div>
                <div className={`mt-1 text-xs font-semibold ${activeCategory === 'all' ? 'text-slate-300' : 'text-slate-500'}`}>
                  {t.articles.length} {lang === 'en' ? 'answers' : 'réponses'}
                </div>
              </button>

              {categories.map((category) => (
                <button
                  key={category.key}
                  onClick={() => setActiveCategory(category.key)}
                  className={`w-full text-left rounded-2xl border px-4 py-4 transition overflow-hidden relative ${
                    activeCategory === category.key
                      ? 'bg-white border-blue-200 shadow-xl shadow-blue-100/50'
                      : 'bg-white border-slate-100 hover:border-blue-200 hover:shadow-md'
                  }`}
                >
                  {activeCategory === category.key && (
                    <div className={`absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b ${category.accent}`} />
                  )}
                  <div className="flex items-start gap-3">
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${category.accent} text-white flex items-center justify-center shrink-0 shadow-lg`}>
                      {category.icon}
                    </div>
                    <div>
                      <div className="font-black text-slate-900">{category.label}</div>
                      <div className="text-xs text-slate-500 font-semibold mt-1 leading-relaxed">{category.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </aside>

            <div className="space-y-4">
              {filtered.length === 0 ? (
                <div className="bg-white rounded-[2rem] border border-slate-100 p-10 text-center shadow-xl shadow-slate-200/40">
                  <FaExclamationTriangle className="text-amber-500 text-4xl mx-auto mb-4" />
                  <h3 className="text-2xl font-black text-slate-900 mb-2">{t.noResultTitle}</h3>
                  <p className="text-slate-500 font-semibold">{t.noResultText}</p>
                </div>
              ) : (
                filtered.map((item, index) => {
                  const isOpen = open === index;
                  const category = categories.find((cat) => cat.key === item.category);
                  return (
                    <article key={`${item.category}-${item.q}`} className="bg-white rounded-[2rem] border border-slate-100 shadow-lg shadow-slate-200/40 overflow-hidden">
                      <button
                        onClick={() => setOpen(isOpen ? -1 : index)}
                        className="w-full px-5 md:px-7 py-5 md:py-6 text-left flex items-center justify-between gap-4 hover:bg-slate-50 transition"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`hidden sm:flex w-12 h-12 rounded-2xl bg-gradient-to-br ${category?.accent || 'from-blue-500 to-cyan-500'} text-white items-center justify-center shrink-0 shadow-lg`}>
                            {category?.icon || <FaQuestionCircle />}
                          </div>
                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span className="text-[10px] font-black uppercase tracking-widest text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">
                                {category?.label}
                              </span>
                              {index < 4 && (
                                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                                  <FaCheckCircle /> FAQ
                                </span>
                              )}
                            </div>
                            <h3 className="text-base md:text-lg font-black text-slate-900 leading-snug">{item.q}</h3>
                          </div>
                        </div>
                        <div className="shrink-0 text-slate-400">
                          {isOpen ? <FaChevronUp className="text-blue-600" /> : <FaChevronDown />}
                        </div>
                      </button>

                      {isOpen && (
                        <div className="px-5 md:px-7 pb-6 md:pb-7 text-slate-600 leading-relaxed">
                          <div className="border-t border-slate-100 pt-5 md:pl-16">
                            <p>{item.a}</p>
                            {item.cta && (
                              <Link
                                to={item.cta.to}
                                className="inline-flex items-center gap-2 mt-5 bg-slate-950 text-white px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition"
                              >
                                {item.cta.label} <FaArrowRight />
                              </Link>
                            )}
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })
              )}
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-6">
          <div className="relative overflow-hidden bg-slate-950 text-white rounded-[2.5rem] p-8 md:p-12 shadow-2xl">
            <div className="absolute top-0 right-0 h-72 w-72 rounded-full bg-blue-500 blur-[100px] opacity-40" />
            <div className="relative z-10 grid md:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 text-cyan-200 bg-white/10 border border-white/10 px-4 py-2 rounded-full text-[11px] font-black uppercase tracking-widest mb-5">
                  <FaHeadset /> Support Forfeo
                </div>
                <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4">{t.contactTitle}</h2>
                <p className="text-slate-300 max-w-2xl leading-relaxed">{t.contactText}</p>
                <p className="text-slate-400 text-sm mt-4 font-semibold">client@forfeo.com</p>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-3">
                <a
                  href="mailto:client@forfeo.com?subject=Support%20Forfeo%20Explore"
                  className="inline-flex items-center justify-center gap-3 bg-white text-slate-950 px-7 py-4 rounded-2xl font-black hover:bg-slate-100 transition"
                >
                  <FaEnvelope /> {t.contactButton}
                </a>
                <Link
                  to="/boutique"
                  className="inline-flex items-center justify-center gap-3 bg-white/10 border border-white/10 text-white px-7 py-4 rounded-2xl font-black hover:bg-white/15 transition"
                >
                  {t.backOffers} <FaArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
